
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import Swal from "sweetalert2";

import Sidebar from "../../Component/Sidebar/Sidebar";
import Header from "../../Component/Header/Header";

import { Url } from "../../Component/function/FunctionApi";
import { getCookie } from "../../Pages/Login/Login";

/* =========================
   إعدادات الصفحة
========================= */

const PAGE_SIZES = [20, 50, 100, 200, 500];

const INITIAL_FILTERS = {
  FromDate: "",
  ToDate: "",
  Status: "",
  Search: "",
};

const STATUS_OPTIONS = [
  { value: "", label: "كل الحالات" },
  { value: "جديد", label: "جديد" },
  { value: "تحت التجهيز", label: "تحت التجهيز" },
  { value: "تم التسليم", label: "تم التسليم" },
  { value: "مرتجع", label: "مرتجع" },
  { value: "ملغي", label: "ملغي" },
];

const STATUS_LABELS = {
  New: "جديد",
  Pending: "قيد الانتظار",
  Processing: "تحت التجهيز",
  Completed: "مكتمل",
  Delivered: "تم التسليم",
  Cancelled: "ملغي",
  Returned: "مرتجع",
  Postponed: "مؤجل",
  "تم_التسليم": "تم التسليم",
  "تم التسليم": "تم التسليم",
  "تحت التجهيز": "تحت التجهيز",
  "قيد الانتظار": "قيد الانتظار",
  "مرتجع": "مرتجع",
  "ملغي": "ملغي",
  "مؤجل": "مؤجل",
  "جديد": "جديد",
};

const authHeaders = () => {
  try {
    const user = getCookie("user");
    const token = user ? JSON.parse(user).token : "";

    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
};

const displayValue = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : String(value);

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const number = Number(value);

  if (Number.isNaN(number)) return value;

  return `${number.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })} ج.م`;
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("ar-EG", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusLabel = (status) => {
  if (!status) return "غير محدد";
  return STATUS_LABELS[status] || status;
};

const getStatusColor = (status) => {
  const value = String(status || "").toLowerCase();

  if (
    value.includes("deliver") ||
    value.includes("التسليم") ||
    value.includes("completed")
  ) {
    return "success";
  }

  if (
    value.includes("cancel") ||
    value.includes("ملغي") ||
    value.includes("مرتجع") ||
    value.includes("return")
  ) {
    return "error";
  }

  if (
    value.includes("postpon") ||
    value.includes("مؤجل") ||
    value.includes("late")
  ) {
    return "warning";
  }

  return "primary";
};

/* =========================
   عناصر موديل التفاصيل
========================= */

const DetailField = ({ label, value }) => (
  <div className="min-w-0 rounded-lg border border-gray-100 bg-gray-50 p-3">
    <p className="text-xs font-semibold text-gray-500">{label}</p>
    <p className="mt-1 break-words text-sm font-medium text-gray-800">
      {displayValue(value)}
    </p>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="mb-3 border-r-4 border-[#792468] pr-3 text-base font-bold text-gray-800">
    {children}
  </h3>
);

/* =========================
   موديل تفاصيل الأوردر
========================= */

const OrderDetailsModal = ({ order, onClose }) => {
  if (!order) return null;

  const products = Array.isArray(order.invoiceCategories)
    ? order.invoiceCategories
    : [];

  const totals = products.reduce(
    (acc, item) => ({
      quantity: acc.quantity + Number(item.quantity || 0),
      total: acc.total + Number(item.total || 0),
      totalWithShipping:
        acc.totalWithShipping + Number(item.totalWithShipping || 0),
    }),
    { quantity: 0, total: 0, totalWithShipping: 0 }
  );

  return (
    <Modal open={Boolean(order)} onClose={onClose} dir="rtl">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "96vw", md: 960 },
          maxHeight: "92vh",
          overflowY: "auto",
          bgcolor: "white",
          borderRadius: 3,
          outline: "none",
          boxShadow: 24,
        }}
      >
        {/* عنوان الموديل */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-5 py-4">
          <div>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              البيانات الكاملة للأوردر
            </Typography>

            <p className="mt-1 text-sm text-gray-500">
              رقم الفاتورة: {displayValue(order.invoiceCode)}
            </p>
          </div>

          <IconButton onClick={onClose} aria-label="إغلاق">
            <CloseIcon />
          </IconButton>
        </div>

        <div className="space-y-6 p-4 sm:p-6">
          {/* بيانات الأوردر */}
          <section>
            <SectionTitle>بيانات الأوردر</SectionTitle>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <DetailField
                label="رقم الفاتورة"
                value={order.invoiceCode}
              />

              <DetailField
                label="رقم أوردر الموقع"
                value={order.wooCommerceOrderId}
              />

              <DetailField
                label="تاريخ الفاتورة"
                value={formatDate(order.invoiceDate)}
              />

              <DetailField
                label="رقم الأوردر"
                value={order.number}
              />

              <DetailField
                label="مصدر الأوردر"
                value={order.page}
              />

              <DetailField
                label="تاريخ الإنشاء"
                value={formatDate(order.dateCreated)}
              />

              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                <p className="mb-2 text-xs font-semibold text-gray-500">
                  حالة الأوردر
                </p>

                <Chip
                  size="small"
                  label={getStatusLabel(order.status)}
                  color={getStatusColor(order.status)}
                />
              </div>
            </div>

            {order.note && (
              <div className="mt-3 rounded-lg border border-gray-200 p-3">
                <p className="mb-1 text-xs font-semibold text-gray-500">
                  ملاحظات الأوردر
                </p>
                <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
                  {order.note}
                </p>
              </div>
            )}

            {order.statusCause && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="mb-1 text-xs font-semibold text-amber-700">
                  سبب الحالة
                </p>
                <p className="text-sm text-gray-800">
                  {order.statusCause}
                </p>
              </div>
            )}
          </section>

          {/* بيانات العميل */}
          <section>
            <SectionTitle>بيانات العميل</SectionTitle>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <DetailField
                label="اسم العميل"
                value={order.customer}
              />

              <DetailField
                label="رقم الهاتف"
                value={order.phone}
              />

              <DetailField
                label="رقم هاتف إضافي"
                value={order.socendPhone}
              />

              <div className="sm:col-span-2 lg:col-span-3">
                <DetailField
                  label="العنوان"
                  value={order.address}
                />
              </div>
            </div>
          </section>

          {/* الشحن والتوصيل */}
          <section>
            <SectionTitle>الشحن والتوصيل</SectionTitle>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <DetailField
                label="المحافظة"
                value={order.governorate?.name}
              />

              <DetailField
                label="تكلفة الشحن"
                value={formatMoney(order.shipping)}
              />

              <DetailField
                label="مدة التوصيل المتوقعة"
                value={
                  order.governorate?.deliveryDays != null
                    ? `${order.governorate.deliveryDays} يوم`
                    : "—"
                }
              />

              <DetailField
                label="المندوب"
                value={
                  typeof order.representative === "object"
                    ? order.representative?.fullName
                    : order.representative
                }
              />

              <DetailField
                label="تاريخ المندوب"
                value={formatDate(order.dailyDate)}
              />
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {order.isUrgent && (
                <Chip label="أوردر مستعجل" color="error" size="small" />
              )}

              {order.isLate && (
                <Chip label="أوردر متأخر" color="warning" size="small" />
              )}

              {order.notReplied && (
                <Chip label="لم يتم الرد عليه" size="small" />
              )}

              {order.isPostponed && (
                <Chip label="أوردر مؤجل" color="info" size="small" />
              )}
            </div>

            {order.isPostponed && order.postponedUntil && (
              <div className="mt-3">
                <DetailField
                  label="موعد التأجيل"
                  value={formatDate(order.postponedUntil)}
                />
              </div>
            )}
          </section>

          {/* المنتجات */}
          <section>
            <SectionTitle>المنتجات ({products.length})</SectionTitle>

            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full min-w-[650px] text-right text-sm">
                <thead className="bg-gray-50 text-xs font-bold text-gray-600">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3">
                      اسم المنتج
                    </th>
                    <th className="whitespace-nowrap px-4 py-3">
                      الكمية
                    </th>
                    <th className="whitespace-nowrap px-4 py-3">
                      السعر
                    </th>
                    <th className="whitespace-nowrap px-4 py-3">
                      الخصم
                    </th>
                    <th className="whitespace-nowrap px-4 py-3">
                      الإجمالي
                    </th>
                    <th className="whitespace-nowrap px-4 py-3">
                      الإجمالي شامل الشحن
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {products.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="p-5 text-center text-gray-500"
                      >
                        لا توجد منتجات مرتبطة بهذا الأوردر.
                      </td>
                    </tr>
                  ) : (
                    products.map((item, index) => (
                      <tr
                        key={item.id ?? `${item.categoryId}-${index}`}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 font-medium text-gray-800">
                          {item.category?.name || "غير محدد"}
                        </td>

                        <td className="px-4 py-3">
                          {displayValue(item.quantity)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {formatMoney(item.price)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          {formatMoney(item.discount)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 font-semibold">
                          {formatMoney(item.total)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 font-semibold text-green-700">
                          {formatMoney(item.totalWithShipping)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {products.length > 0 && (
                  <tfoot className="bg-green-50 font-bold">
                    <tr>
                      <td className="px-4 py-3">الإجمالي</td>
                      <td className="px-4 py-3">{totals.quantity}</td>
                      <td colSpan={2} />
                      <td className="whitespace-nowrap px-4 py-3">
                        {formatMoney(totals.total)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-green-700">
                        {formatMoney(totals.totalWithShipping)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>

          {/* ملخص الحساب */}
          <section>
            <SectionTitle>ملخص الحساب</SectionTitle>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <DetailField
                label="إجمالي المنتجات"
                value={formatMoney(totals.total)}
              />

              <DetailField
                label="تكلفة الشحن"
                value={formatMoney(order.shipping)}
              />

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs font-semibold text-blue-700">
                  إجمالي الفاتورة
                </p>
                <p className="mt-1 text-lg font-bold text-blue-800">
                  {formatMoney(order.totalPrice)}
                </p>
              </div>
            </div>
          </section>
        </div>

        <div className="flex justify-end border-t bg-gray-50 p-4">
          <Button variant="contained" onClick={onClose}>
            إغلاق
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

/* =========================
   الصفحة الرئيسية
========================= */

const WoCommerce = () => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);

  const [apiResult, setApiResult] = useState({
    data: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 20,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = useCallback(
    async (page = 1, pageSize = 20, currentFilters = appliedFilters) => {
      setLoading(true);
      setError("");

      try {
        const params = {
          Page: page,
          PageSize: pageSize,
        };

        // كل فلتر منفصل وباسم الـ API الصحيح
        if (currentFilters.FromDate) {
          params.FromDate = currentFilters.FromDate;
        }

        if (currentFilters.ToDate) {
          params.ToDate = currentFilters.ToDate;
        }

        if (currentFilters.Status) {
          params.Status = currentFilters.Status;
        }

        if (currentFilters.Search.trim()) {
          params.Search = currentFilters.Search.trim();
        }

        const response = await axios.get(
          `${Url}woocommerce/orders`,
          {
            headers: authHeaders(),
            params,
          }
        );

        const payload = response.data || {};

        setApiResult({
          data: Array.isArray(payload.data) ? payload.data : [],
          totalCount: Number(payload.totalCount) || 0,
          totalPages: Number(payload.totalPages) || 0,
          currentPage: Number(payload.currentPage) || page,
          pageSize: Number(payload.pageSize) || pageSize,
          hasPreviousPage: Boolean(payload.hasPreviousPage),
          hasNextPage: Boolean(payload.hasNextPage),
        });
      } catch (err) {
        if (axios.isCancel(err)) return;

        const message =
          err?.response?.data?.message ||
          "حدث خطأ أثناء تحميل أوردرات الموقع.";

        setError(message);

        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: message,
          confirmButtonText: "حسنًا",
        });
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters]
  );

  useEffect(() => {
    fetchOrders(1, 20, INITIAL_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSearch = (event) => {
    event.preventDefault();

    if (
      filters.FromDate &&
      filters.ToDate &&
      filters.FromDate > filters.ToDate
    ) {
      Swal.fire({
        icon: "warning",
        title: "التاريخ غير صحيح",
        text: "تاريخ البداية يجب أن يكون قبل تاريخ النهاية.",
        confirmButtonText: "حسنًا",
      });
      return;
    }

    const nextFilters = { ...filters };

    setAppliedFilters(nextFilters);
    fetchOrders(1, apiResult.pageSize, nextFilters);
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    fetchOrders(1, apiResult.pageSize, INITIAL_FILTERS);
  };

  const handlePageSizeChange = (event) => {
    fetchOrders(1, Number(event.target.value), appliedFilters);
  };

  const handlePageChange = (page) => {
    if (
      loading ||
      page < 1 ||
      page > apiResult.totalPages
    ) {
      return;
    }

    fetchOrders(page, apiResult.pageSize, appliedFilters);
  };

  const rows = apiResult.data;
  const totalPages = apiResult.totalPages;

  const firstRow =
    apiResult.totalCount === 0
      ? 0
      : (apiResult.currentPage - 1) * apiResult.pageSize + 1;

  const lastRow = Math.min(
    apiResult.currentPage * apiResult.pageSize,
    apiResult.totalCount
  );

  const pageItems = (() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const result = [1];
    const start = Math.max(2, apiResult.currentPage - 1);
    const end = Math.min(totalPages - 1, apiResult.currentPage + 1);

    if (start > 2) result.push("...");

    for (let page = start; page <= end; page += 1) {
      result.push(page);
    }

    if (end < totalPages - 1) result.push("...");

    result.push(totalPages);

    return result;
  })();

  return (
    <div className="apDiv searchInvoices woCommercePage">
      <Sidebar />
      <div className="body_container container" dir="rtl">
        <div className="Header">
          <Header isSearch="false" title="أوردرات الموقع" />
        </div>
        <div dir="rtl" className="w-full">
      <Paper elevation={0} className="overflow-hidden rounded-xl border border-gray-200">
        {/* عنوان الصفحة */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              أوردرات الموقع
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              متابعة أوردرات WooCommerce والبحث عنها
            </p>
          </div>

          <Tooltip title="تحديث البيانات">
            <span>
              <IconButton
                disabled={loading}
                onClick={() =>
                  fetchOrders(
                    apiResult.currentPage,
                    apiResult.pageSize,
                    appliedFilters
                  )
                }
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        {/* الفلاتر: كل فلتر مستقل وظاهر */}
        <form
          onSubmit={handleSearch}
          className="grid grid-cols-1 gap-4 border-b border-gray-200 bg-white p-4 sm:grid-cols-2 xl:grid-cols-4"
        >
          <div>
            <label
              htmlFor="woo-from-date"
              className="mb-2 block text-sm font-bold text-gray-700"
            >
              من تاريخ
            </label>

            <input
              id="woo-from-date"
              type="date"
              name="FromDate"
              value={filters.FromDate}
              onChange={handleFilterChange}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div>
            <label
              htmlFor="woo-to-date"
              className="mb-2 block text-sm font-bold text-gray-700"
            >
              إلى تاريخ
            </label>

            <input
              id="woo-to-date"
              type="date"
              name="ToDate"
              value={filters.ToDate}
              onChange={handleFilterChange}
              min={filters.FromDate || undefined}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div>
            <label
              htmlFor="woo-status"
              className="mb-2 block text-sm font-bold text-gray-700"
            >
              الحالة
            </label>

            <select
              id="woo-status"
              name="Status"
              value={filters.Status}
              onChange={handleFilterChange}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="woo-search"
              className="mb-2 block text-sm font-bold text-gray-700"
            >
              بحث
            </label>

            <input
              id="woo-search"
              type="search"
              name="Search"
              value={filters.Search}
              onChange={handleFilterChange}
              placeholder="رقم الفاتورة أو اسم العميل أو الهاتف..."
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="flex flex-wrap gap-2 sm:col-span-2 xl:col-span-4">
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#792468",
                "&:hover": { backgroundColor: "#612052" },
              }}
            >
              بحث
            </Button>

            <Button
              type="button"
              variant="outlined"
              disabled={loading}
              onClick={handleClearFilters}
            >
              مسح الفلاتر
            </Button>
          </div>
        </form>

        {/* كارت الإجمالي */}
        <div className="mx-4 mt-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-xl font-bold text-white">
              #
            </div>

            <div>
              <p className="text-sm text-gray-600">
                إجمالي عدد الأوردرات
              </p>
              <p className="text-2xl font-bold text-blue-700">
                {apiResult.totalCount.toLocaleString("en-US")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label
              htmlFor="woo-page-size"
              className="text-sm font-semibold text-gray-700"
            >
              عدد الأوردرات في الصفحة
            </label>

            <select
              id="woo-page-size"
              value={apiResult.pageSize}
              onChange={handlePageSizeChange}
              disabled={loading}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size} أوردر
                </option>
              ))}
            </select>
          </div>

          <div className="text-sm text-gray-600">
            المعروض حاليًا:{" "}
            <span className="font-bold text-gray-900">{rows.length}</span>
            {" من "}
            <span className="font-bold text-gray-900">
              {apiResult.totalCount}
            </span>
          </div>
        </div>

        {/* الجدول */}
        <div className="relative mt-4">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
              <CircularProgress />
            </div>
          )}

          {error && rows.length === 0 && !loading && (
            <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <p>{error}</p>
              <Button
                sx={{ mt: 1 }}
                variant="outlined"
                onClick={() =>
                  fetchOrders(
                    apiResult.currentPage,
                    apiResult.pageSize,
                    appliedFilters
                  )
                }
              >
                إعادة المحاولة
              </Button>
            </div>
          )}

          {error && rows.length > 0 && (
            <p className="mx-4 mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="w-full overflow-x-auto">
            <table className="min-w-[1100px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    "#",
                    "رقم الفاتورة",
                    "رقم أوردر الموقع",
                    "تاريخ الأوردر",
                    "العميل",
                    "الهاتف",
                    "المحافظة",
                    "المنتجات",
                    "الإجمالي",
                    "الحالة",
                    "التفاصيل",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="whitespace-nowrap px-4 py-3 text-right text-xs font-bold text-gray-600"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((order, index) => {
                  const productNames = (
                    order.invoiceCategories || []
                  )
                    .map((item) => item.category?.name)
                    .filter(Boolean)
                    .join("، ");

                  return (
                    <tr
                      key={order.id ?? order.wooCommerceOrderId ?? index}
                      className="transition hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                        {(apiResult.currentPage - 1) *
                          apiResult.pageSize +
                          index +
                          1}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 font-bold text-gray-800">
                        {displayValue(order.invoiceCode)}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {displayValue(order.wooCommerceOrderId)}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {formatDate(order.invoiceDate)}
                      </td>

                      <td className="max-w-[200px] px-4 py-3">
                        <p className="truncate font-medium text-gray-800">
                          {displayValue(order.customer)}
                        </p>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {displayValue(order.phone)}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {displayValue(order.governorate?.name)}
                      </td>

                      <td className="max-w-[220px] px-4 py-3">
                        <p className="truncate" title={productNames}>
                          {productNames || "—"}
                        </p>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 font-semibold">
                        {formatMoney(order.totalPrice)}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <Chip
                          size="small"
                          variant="outlined"
                          color={getStatusColor(order.status)}
                          label={getStatusLabel(order.status)}
                        />
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setSelectedOrder(order)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-[#792468] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#612052]"
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                          التفاصيل
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {!loading && rows.length === 0 && !error && (
                  <tr>
                    <td
                      colSpan={11}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      لا توجد أوردرات مطابقة للفلاتر المحددة.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex flex-col items-center justify-center gap-4 border-t border-gray-200 py-5">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(1)}
              disabled={loading || apiResult.currentPage <= 1}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              الأولى
            </button>

            <button
              type="button"
              onClick={() =>
                handlePageChange(apiResult.currentPage - 1)
              }
              disabled={loading || !apiResult.hasPreviousPage}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              السابق
            </button>

            {pageItems.map((item, index) =>
              item === "..." ? (
                <span key={`dots-${index}`} className="px-1 text-gray-400">
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => handlePageChange(item)}
                  disabled={loading}
                  aria-current={
                    item === apiResult.currentPage ? "page" : undefined
                  }
                  className={`h-10 w-10 rounded-lg text-sm font-semibold transition ${
                    item === apiResult.currentPage
                      ? "bg-blue-600 text-white"
                      : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  } disabled:opacity-50`}
                >
                  {item}
                </button>
              )
            )}

            <button
              type="button"
              onClick={() =>
                handlePageChange(apiResult.currentPage + 1)
              }
              disabled={loading || !apiResult.hasNextPage}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              التالي
            </button>

            <button
              type="button"
              onClick={() => handlePageChange(totalPages)}
              disabled={loading || !apiResult.hasNextPage}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              الأخيرة
            </button>
          </div>

          <p className="text-sm text-gray-600">
            عرض {firstRow} إلى {lastRow} من {apiResult.totalCount} أوردر
            {" — "}
            صفحة {apiResult.currentPage} من {Math.max(1, totalPages)}
          </p>
        </div>
      </Paper>

      {/* موديل تفاصيل الأوردر */}
          <OrderDetailsModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
          />
        </div>
      </div>
    </div>
  );
};

export default WoCommerce;
